import { emptyFilters } from '@mona-roadmap/domain';
import type { FilterState, SortState, ViewMode } from '@mona-roadmap/domain';
import { useCallback, useEffect, useRef, useState } from 'react';
import { buildSearch, initialUrlState } from '../lib/urlState';

/** View + filters + search live in the URL query (replaceState; shareable) and apply client-side instantly. */
export function useUrlState() {
  const [state, setState] = useState(initialUrlState);
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const next = buildSearch(window.location.search, state);
    if (next !== window.location.search) window.history.replaceState(window.history.state, '', `${window.location.pathname}${next}${window.location.hash}`);
  }, [state]);

  useEffect(() => {
    const onPop = () => setState(initialUrlState());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const setView = useCallback((view: ViewMode) => setState((s) => (s.view === view ? s : { ...s, view })), []);
  const patchFilters = useCallback((patch: Partial<FilterState>) => setState((s) => ({ ...s, filters: { ...s.filters, ...patch } })), []);
  const setSort = useCallback((sort: SortState) => setState((s) => ({ ...s, sort })), []);
  const clearFilters = useCallback(() => setState((s) => ({ ...s, filters: emptyFilters() })), []);

  return { view: state.view, filters: state.filters, sort: state.sort, setSort, setView, patchFilters, clearFilters };
}
